import React, { useState, useCallback, useRef } from 'react';
import { useCanvasAnimation } from '../../../hooks/useCanvasAnimation';

interface ElectronPos {
  x: number;
  y: number;
}

export function ElectronDegeneracy() {
  const [gravity, setGravity] = useState<number>(0);

  // Particles position memory for smooth sliding transitions
  const particlesRef = useRef<ElectronPos[]>([]);

  const draw = useCallback((ctx: CanvasRenderingContext2D, deltaTime: number, totalTime: number) => {
    const width = ctx.canvas.clientWidth;
    const height = ctx.canvas.clientHeight;

    // Clear background to #0F172A
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, width, height);

    const cx = width * 0.4;
    const cy = height / 2 - 10;

    // Box dimensions
    const boxWidth = 200;
    const boxHeight = 180 - (gravity / 100) * 130; // 180px down to 50px
    const left = cx - boxWidth / 2;
    const right = cx + boxWidth / 2;
    const top = cy - boxHeight / 2;
    const bottom = cy + boxHeight / 2;

    // 1. CALCULATE ELECTRON GRID TARGETS
    // 12 electrons arranged in cols x rows
    let cols = 3;
    let rows = 4;
    if (gravity >= 85) {
      cols = 6;
      rows = 2;
    } else if (gravity >= 45) {
      cols = 4;
      rows = 3;
    }

    const targets: ElectronPos[] = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (targets.length >= 12) break;
        const tx = left + (c + 0.5) * (boxWidth / cols);
        const ty = top + (r + 0.5) * (boxHeight / rows);
        targets.push({ x: tx, y: ty });
      }
    }

    // Initialize or lerp particles positions
    if (particlesRef.current.length === 0) {
      particlesRef.current = targets.map((t) => ({ ...t }));
    } else {
      particlesRef.current.forEach((p, i) => {
        const t = targets[i] || targets[targets.length - 1];
        // Smooth lerping to grid targets
        p.x += (t.x - p.x) * deltaTime * 12.0;
        p.y += (t.y - p.y) * deltaTime * 12.0;
      });
    }

    // 2. DRAW DEGENERACY PRESSURE GAUGE (Right Side)
    const gx = width * 0.8;
    const gy = cy - 90;
    const gWidth = 20;
    const gHeight = 180;

    // Degeneracy pressure calculation
    const degenPressure = gravity > 0 ? Math.pow(gravity / 100, 2) * 100 : 0;

    // Gauge track
    ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
    ctx.beginPath();
    ctx.roundRect(gx, gy, gWidth, gHeight, 6);
    ctx.fill();
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.roundRect(gx, gy, gWidth, gHeight, 6);
    ctx.stroke();

    // Gauge fill
    if (degenPressure > 0) {
      const fillH = (degenPressure / 100) * gHeight;
      const gGrad = ctx.createLinearGradient(gx, gy + gHeight, gx, gy);
      gGrad.addColorStop(0, '#A78BFA'); // Light purple
      gGrad.addColorStop(1, '#C084FC'); // Bright purple
      ctx.fillStyle = gGrad;
      ctx.beginPath();
      ctx.roundRect(gx + 2, gy + gHeight - fillH + 2, gWidth - 4, fillH - 4, Math.min(4, fillH / 2));
      ctx.fill();
    }

    // Gauge Labels
    ctx.fillStyle = '#A78BFA';
    ctx.font = 'bold 9px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('DEGENERACY', gx + gWidth / 2, gy - 24);
    ctx.fillText('PRESSURE', gx + gWidth / 2, gy - 12);

    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 12px sans-serif';
    ctx.fillText(`${degenPressure.toFixed(0)}%`, gx + gWidth / 2, gy + gHeight + 20);

    // 3. DRAW THE COMPRESSIBLE BOX
    // Outer dashed frame
    ctx.strokeStyle = '#94A3B8';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(left, top, boxWidth, boxHeight, 8);
    ctx.stroke();

    // TOP WALL (gravity force)
    ctx.fillStyle = '#F87171'; // light red
    ctx.beginPath();
    ctx.roundRect(left - 4, top - 6, boxWidth + 8, 12, 3);
    ctx.fill();

    // Gravity Arrow & Text above top wall
    const arrowY = top - 14;
    ctx.strokeStyle = '#F87171';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(cx, arrowY - 24);
    ctx.lineTo(cx, arrowY);
    ctx.moveTo(cx - 5, arrowY - 6);
    ctx.lineTo(cx, arrowY);
    ctx.lineTo(cx + 5, arrowY - 6);
    ctx.stroke();

    ctx.fillStyle = '#F87171';
    ctx.font = 'bold 10px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('GRAVITY', cx, arrowY - 28);

    // BOTTOM WALL (fixed wall)
    ctx.fillStyle = '#60A5FA'; // light blue
    ctx.beginPath();
    ctx.roundRect(left - 4, bottom - 6, boxWidth + 8, 12, 3);
    ctx.fill();

    // Degeneracy pressure pushback arrow when gravity > 20
    if (gravity > 20) {
      const dbArrowY = bottom + 14;
      ctx.strokeStyle = '#A78BFA';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(cx, dbArrowY + 24);
      ctx.lineTo(cx, dbArrowY);
      ctx.moveTo(cx - 5, dbArrowY + 6);
      ctx.lineTo(cx, dbArrowY);
      ctx.lineTo(cx + 5, dbArrowY + 6);
      ctx.stroke();

      ctx.fillStyle = '#A78BFA';
      ctx.font = 'bold 9px sans-serif';
      ctx.fillText('DEGENERACY PRESSURE', cx, dbArrowY + 34);
    }

    // 4. DRAW ELECTRONS
    const isSqueezed = boxHeight < 100;
    particlesRef.current.forEach((p) => {
      // Glow and color adjustment based on squeezing
      ctx.fillStyle = isSqueezed ? '#F5F3FF' : '#A78BFA';
      
      if (isSqueezed) {
        ctx.shadowColor = '#C084FC';
        ctx.shadowBlur = 10;
      }
      
      ctx.beginPath();
      ctx.arc(p.x, p.y, 8, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.shadowBlur = 0; // reset glow

      // Label inside electron
      ctx.fillStyle = isSqueezed ? '#6B21A8' : '#FFFFFF';
      ctx.font = 'bold 10px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('e⁻', p.x, p.y + 3);

      // Force push arrows (North, South, East, West) when squeezed
      if (isSqueezed) {
        ctx.strokeStyle = 'rgba(192, 132, 252, 0.85)';
        ctx.lineWidth = 1.5;
        const arrowLen = 6;
        
        // North
        ctx.beginPath();
        ctx.moveTo(p.x, p.y - 8);
        ctx.lineTo(p.x, p.y - 8 - arrowLen);
        ctx.moveTo(p.x - 3, p.y - 8 - arrowLen + 3);
        ctx.lineTo(p.x, p.y - 8 - arrowLen);
        ctx.lineTo(p.x + 3, p.y - 8 - arrowLen + 3);
        ctx.stroke();

        // South
        ctx.beginPath();
        ctx.moveTo(p.x, p.y + 8);
        ctx.lineTo(p.x, p.y + 8 + arrowLen);
        ctx.moveTo(p.x - 3, p.y + 8 + arrowLen - 3);
        ctx.lineTo(p.x, p.y + 8 + arrowLen);
        ctx.lineTo(p.x + 3, p.y + 8 + arrowLen - 3);
        ctx.stroke();

        // East
        ctx.beginPath();
        ctx.moveTo(p.x + 8, p.y);
        ctx.lineTo(p.x + 8 + arrowLen, p.y);
        ctx.moveTo(p.x + 8 + arrowLen - 3, p.y - 3);
        ctx.lineTo(p.x + 8 + arrowLen, p.y);
        ctx.lineTo(p.x + 8 + arrowLen - 3, p.y + 3);
        ctx.stroke();

        // West
        ctx.beginPath();
        ctx.moveTo(p.x - 8, p.y);
        ctx.lineTo(p.x - 8 - arrowLen, p.y);
        ctx.moveTo(p.x - 8 - arrowLen + 3, p.y - 3);
        ctx.lineTo(p.x - 8 - arrowLen, p.y);
        ctx.lineTo(p.x - 8 - arrowLen + 3, p.y + 3);
        ctx.stroke();
      }
    });

    // 5. MAX SQUEEZE WARNING
    if (gravity >= 99) {
      ctx.fillStyle = 'rgba(239, 68, 68, 0.1)';
      ctx.beginPath();
      ctx.roundRect(left, top, boxWidth, boxHeight, 8);
      ctx.fill();

      ctx.fillStyle = '#EF4444';
      ctx.font = 'bold 9px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Cannot compress further!', cx, cy - 2);
      ctx.fillText('(Pauli Exclusion Principle)', cx, cy + 8);
    }

    // Top-Left Title Label
    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Pauli Exclusion Principle & Electron Degeneracy', 12, 24);
  }, [gravity]);

  const canvasRef = useCanvasAnimation(draw, { height: 340 });

  return (
    <div className="relative w-full h-[340px] rounded-xl overflow-hidden bg-[#0F172A] border border-white/10 select-none">
      <canvas ref={canvasRef} className="block w-full h-full" />

      {/* Control overlay */}
      <div className="absolute bottom-3 inset-x-3 bg-[#0b0f19]/80 backdrop-blur-sm p-3 rounded-lg border border-white/10 flex items-center gap-3">
        <div className="flex-1 flex flex-col gap-1">
          <span className="text-white text-[10px] uppercase font-bold tracking-wider opacity-60">
            Gravity Compression: {gravity.toFixed(0)}%
          </span>
          <input
            type="range"
            min="0"
            max="100"
            step="1"
            value={gravity}
            onChange={(e) => setGravity(parseFloat(e.target.value))}
            className="w-full accent-red-400 cursor-pointer h-1.5 bg-white/20 rounded-lg appearance-none"
          />
        </div>
        
        <button
          onClick={() => setGravity(0)}
          className="px-3 py-1.5 rounded bg-white/10 hover:bg-white/20 text-white text-xs font-bold transition-all border border-white/10 uppercase tracking-wide"
        >
          Reset
        </button>
      </div>
    </div>
  );
}
